import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface TaskFormFieldsProps {
  formData: {
    title: string;
    assigned_to: string;
    date: string;
    location: string;
    priority: string;
    task_type: string;
    estimated_hours: number;
    actual_hours?: number;
    created_by_type: 'admin' | 'employee';
    task_description?: string;
  };
  setFormData: (data: any) => void;
  isAdmin: boolean;
}

const TaskFormFields = ({ formData, setFormData, isAdmin }: TaskFormFieldsProps) => {
  const [employees, setEmployees] = useState<Array<{ id: string; email: string; first_name: string; last_name: string }>>([]);

  useEffect(() => {
    // Set default date to today when form is initialized
    if (!formData.date) {
      const today = new Date().toISOString().split('T')[0];
      setFormData({ ...formData, date: today });
    }

    const fetchEmployees = async () => {
      try {
        const { data: employeeRoles, error: rolesError } = await supabase
          .from('user_roles')
          .select('user_id')
          .eq('role', 'employee');

        if (rolesError) throw rolesError;

        if (employeeRoles) {
          const employeeIds = employeeRoles.map(role => role.user_id);
          const { data: employeeProfiles, error: profilesError } = await supabase
            .from('profiles')
            .select('id, email, first_name, last_name')
            .in('id', employeeIds);

          if (profilesError) throw profilesError;

          if (employeeProfiles) {
            setEmployees(employeeProfiles);
          }
        }
      } catch (error) {
        console.error('Fejl ved hentning af medarbejdere:', error);
      }
    };

    if (isAdmin) {
      fetchEmployees();
    }
  }, [isAdmin]);

  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="title">Titel</Label>
        <Input
          id="title"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          required
          className="bg-white"
        />
      </div>

      <div>
        <Label htmlFor="task_description">Beskrivelse</Label>
        <Textarea
          id="task_description"
          value={formData.task_description || ''}
          onChange={(e) => setFormData({ ...formData, task_description: e.target.value })}
          placeholder="Tilføj en beskrivelse af opgaven..."
          className="bg-white min-h-[100px]"
        />
      </div>

      <div>
        <Label htmlFor="assigned_to">
          {isAdmin ? 'Tildelt til' : 'Med aftale af'}
        </Label>
        {isAdmin ? (
          <Select
            value={formData.assigned_to}
            onValueChange={(value) => setFormData({ ...formData, assigned_to: value })}
          >
            <SelectTrigger className="w-full bg-white">
              <SelectValue placeholder="Vælg medarbejder" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              {employees.map((employee) => (
                <SelectItem 
                  key={employee.id} 
                  value={employee.email}
                >
                  {employee.first_name} {employee.last_name} ({employee.email})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Input
            id="assigned_to"
            value={formData.assigned_to}
            onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
            required
            className="bg-white"
          />
        )}
      </div>

      <div>
        <Label htmlFor="date">Dato</Label>
        <Input
          id="date"
          type="date"
          value={formData.date}
          onChange={(e) => setFormData({ ...formData, date: e.target.value })}
          required
          min={new Date().toISOString().split('T')[0]}
          className="bg-white"
        />
      </div>

      <div>
        <Label htmlFor="location">Lokation</Label>
        <Input
          id="location"
          value={formData.location}
          onChange={(e) => setFormData({ ...formData, location: e.target.value })}
          required
          className="bg-white"
        />
      </div>

      {isAdmin && (
        <div>
          <Label htmlFor="priority">Prioritet</Label>
          <Select
            value={formData.priority}
            onValueChange={(value) => setFormData({ ...formData, priority: value })}
          >
            <SelectTrigger className="w-full bg-white">
              <SelectValue placeholder="Vælg prioritet" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="low">Lav</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="high">Høj</SelectItem>
              <SelectItem value="critical">Kritisk</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      <div>
        <Label htmlFor="task_type">Opgavetype</Label>
        <Select
          value={formData.task_type}
          onValueChange={(value) => setFormData({ ...formData, task_type: value })}
        >
          <SelectTrigger className="w-full bg-white">
            <SelectValue placeholder="Vælg opgavetype" />
          </SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="installation">Installation</SelectItem>
            <SelectItem value="repair">Reparation</SelectItem>
            <SelectItem value="inspection">Inspektion</SelectItem>
            <SelectItem value="maintenance">Vedligeholdelse</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isAdmin ? (
        <div>
          <Label htmlFor="estimated_hours">Estimerede timer</Label>
          <Input
            id="estimated_hours"
            type="number"
            min="1"
            value={formData.estimated_hours}
            onChange={(e) => setFormData({ ...formData, estimated_hours: parseInt(e.target.value) })}
            required
            className="bg-white"
          />
        </div>
      ) : (
        <div>
          <Label htmlFor="actual_hours">Brugte timer</Label>
          <Input
            id="actual_hours"
            type="number"
            min="0"
            value={formData.actual_hours || 0}
            onChange={(e) => setFormData({ ...formData, actual_hours: parseInt(e.target.value) })}
            required
            className="bg-white"
          />
        </div>
      )}
    </div>
  );
};

export default TaskFormFields;
